import React, { Fragment, useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import InfiniteScroll from '../InfiniteScroll/InfiniteScroll';
import Menu from '../Menu/Menu';
import createFakeEvent from '../../lib/createFakeEvent';
import TableSelectionHandler from './TableSelectionHandler';

/**
 * `Table` is an extension of the HTML `table` element that can be built using `thead`, `tbody`, `tr` and `td` elements, or
 * it can automatically be constructed from your data structure if you pass the *items* and *columns* props.
 *
 * It allows you to set how each row and column should be rendered by providing your own item renderer. By default, the built
 * in renderer will simply show a cell's value for each of the columns provided.
 *
 * You add custom components to cells either by providing a custom item renderer or manually if you provide the `tr` and `td`
 * elements yourself.
 *
 * The `Table` component also supports inifinite scrolling if the className contains "scrollable". See {@link InfiniteScroll}
 * on how to use the *hasMore*, *loadMore* and *pageStart* callbacks to dynamically load more rows. Due to limitations in
 * CSS, if you set the "scrollable" class you must also set the column th and td widths - otherwise they will not be aligned.
 *
 * If the prop *onCreateMenuItems* is provided, the `Table` component will automatically generate an action menu for each
 * row in *items*. The components returned from this callback will be displayed inside the popup menu.
 *
 * Table rows can be selected by setting *selectionType* to either `Table.SINGLE` or `Table.MULTIPLE` for single or
 * multi-range selection respectively. Selection works best when the row items have a unique `id` property. If this `id` is
 * not present, `Table` will use the row index instead, but this will result in row index 5 remaining selected even if row 5
 * is moved up or down in the table due to changes to *items*.
 *
 * @property {node} children The `thead` and `tbody` elements of this table. Use this when you want to create a static
 * table.
 * @property {string} className The class name of this table component. Will be appended to "core-table". Set to
 * "scrollable" if you want the table component to provide infinite scroll functionality. See {@link InfiniteScroll} for more
 * details.
 * @property {array} columns An array of objects that defines which values should be displayed for each object in *items*.
 * @property {array} columnNames By default the property names in *columns* will be displayed, but you may use this prop to
 * provide custom names.
 * @property {boolean} disabled If set to `true` the table will be disabled and you will not be able to select rows or open
 * a row's action menu. Defaults to `false`.
 * @property {function} hasMore A callback that should return `true` when there are more content to be loaded. *loadMore*
 * will only be called as long as *hasMore* returns `true`.
 * @property {function} itemRenderer The renderer that will be used to visualize each row and column in a table. The callback
 * will be passed the item, column name and column index. You may set your own renderer when you want to have custom
 * visualization, or when you want to display nested object values in a row. The default renderer will display all values
 * donated by the *columns* prop for each row's object in *items*.
 * @property {array} items An array of row objects. A column will be created for each of the named properties in *columns*.
 * @property {function} loadMore A callback that triggers loading of more content. *loadMore* will only be called as long as
 * *hasMore* returns `true`.
 * @property {function} onClick A callback that is triggered when the user has clicked on a row. The row object will be
 * passed in `event.target.value`.
 * @property {function} onDoubleClick Callback for whenever a row is double clicked. The `event.target.value` will hold the
 * actual row item that was clicked.
 * @property {function} onCreateMenuItems A callback that is triggered when the user has clicked on a row's action menu. The
 * action menu will only be created when this callback is set and the table content is dynamically built by providing the
 * *columns* and *items* props.
 * @property {function} pageStart A callback which returns the next page index. `InfiniteScroll` will only call *hasMore* and
 * *loadMore* when *pageStart* returns a different page index that is not equal to the last one returned.
 * @property {function} onSelectionChanged Callback for whenever the selection changes. `event.target.value` will be an
 * object that represents the new selection. This object should be passed to as the new *selection* prop.
 * *onSelectionChanged* will not be called when *disabled* is `true`.
 * @property {object} selection An object which holds the keys of all selected rows. The key should be the item's ID if
 * available or the row index. Normally you will not have to maintain this object yourself - just pass whatever is sent to
 * your *onSelectionChanged* callback. Selection should be cleared when clicking on the container outside the table to
 * to prevent sticky selection. See the example below on how to do this. Defaults to no selection.
 * @property {number} selectionType The selection type. Must be one of `Table.NONE`, `Table.SINGLE` or `Table.MULTIPLE`.
 * Defaults to `Table.SINGLE`.
 * @property {number} threshold The number of pixels from the bottom of the container that has to be visible before the
 * callbacks are called. Defaults to 400px.
 * @property {function} itemEditor Used to pass the component that will be replacing the cell text when editing inline
 * (similar to *itemRenderer*). The callback will pass the `item`, `column name` and `column index`. Use Case: You may want
 * to edit a row in place, for which you will typically use *itemEditor* to change the text element by an input. To finish
 * editing, you can click outside which will trigger *onEditCompleted*. *itemEditor* will be called on two clicks when the
 * table is selectable and on single click when is not (*selectionType* is `Table.NONE`). If *itemEditor* returns `false` for
 * a concrete cell, this one will not be editable.
 * @property {function} onEditCompleted Function that will be called when clicking outside the element being edited (see *itemEditor*).
 * This function will typically have the handlers to commit the changes performed during the editing.
 * @property {string} emptyText String that can be used to display a message when there are no results or there has been an error.
 *
 * @see InfiniteScroll
 */
const Table = forwardRef((props, ref) => {
  const { children, className, columns, columnNames, disabled, hasMore, itemEditor, itemRenderer, items, onClick, onCreateMenuItems, // eslint-disable-line no-unused-vars
  onDoubleClick, onSelectionChanged, loadMore, pageStart, selection, selectionType, threshold, onEditCompleted, editInSingleClick, emptyText, ...rest } = props; // eslint-disable-line no-unused-vars
  const DOUBLE_CLICK_THERESHOLD = 500;

  const refTBody = useRef();
  const refCell = useRef([]);
  const [selectionHandler, setSelectionHandler] = useState(new TableSelectionHandler(selectionType));
  const [clickInfo, setClickInfo] = useState({clickTime: 0, clicked: null, editing: null});

  useEffect(() => {
    setSelectionHandler(new TableSelectionHandler(selectionType));
  }, [selectionType]);

  useImperativeHandle(ref, () => ({
    onEditCancelled : () => {
      setClickInfo({ ...clickInfo, clicked: null, editing: null });
    }
  }));

  const getCellIndex = (index, colIndex) => {
    return columns.length * index + colIndex;
  }

  const handleOnClick = (e) => {
    const { clickTime, clicked, editing } = clickInfo;
    const { colname, colindex } = e.target.dataset;
    const rowIndex = parseInt(e.currentTarget.dataset.index, 10);
    const columnIndex = parseInt(colindex, 10);
    const currentCellIndex = getCellIndex(rowIndex, columnIndex);
    const isSingleClick = (new Date() - clickTime) > DOUBLE_CLICK_THERESHOLD;
    const isCellEditable = items && items[rowIndex] && itemEditor &&
      itemEditor(items[rowIndex], colname, columnIndex);

    if (!disabled) {
      e.stopPropagation();
      if (onClick) {
        onClick(createFakeEvent(items[rowIndex], e));
      }
      if (!e.defaultPrevented && onSelectionChanged) {
        const newSelection = selectionHandler.handleSelectionEvent(items, rowIndex, selection, e);

        onSelectionChanged(createFakeEvent(newSelection, e));
      }

      setClickInfo({ ...clickInfo, clickTime: new Date() });

      if (isCellEditable && !editing) {
        if (selectionType === Table.NONE) {
          setClickInfo({ ...clickInfo, clicked: null, editing: currentCellIndex });
        } else if (isSingleClick) {
          if (clicked !== currentCellIndex) {
            setClickInfo({ ...clickInfo, clicked: currentCellIndex });
          } else {
            setClickInfo({ ...clickInfo, clicked: null, editing: currentCellIndex });
          }
        }
      }
    }
  };

  const handleOnDoubleClick = (e) => {
    const index = parseInt(e.currentTarget.dataset.index, 10);

    if (!disabled && onDoubleClick) {
      onDoubleClick(createFakeEvent(items[index], e));
    }
  };

  const handleItemEditor = (item, column, colIndex) => {
    const { editing } = clickInfo;
    refCell.current[editing].setAttribute('height', refCell.current[editing].offsetHeight);
    return itemEditor(item, column, colIndex);
  };

  const handleOnEditCompleted = () => {
    onEditCompleted && onEditCompleted();
    const { editing } = clickInfo;
    refCell.current[editing].removeAttribute('height');
    setClickInfo({ ...clickInfo, clicked: null, editing: null });
  };

  const cls = cx('core-table', className, {
    editable: itemEditor,
    selectable: selectionType !== TableSelectionHandler.NONE && onSelectionChanged, disabled,
    selected: selection && TableSelectionHandler.hasSelection(selection),
  });
  const { editing } = clickInfo;
  const names = columnNames || columns;
  const thead = columns && (
    <thead>
      <tr>
        {columns.map((column, index) => <th key={column} className={column}><span>{names[index]}</span></th>)}
        {onCreateMenuItems && <th className="menu">Menu</th>}
      </tr>
    </thead>
  );
  const tbody = columns && items && (
    <InfiniteScroll component="tbody" hasMore={hasMore} loadMore={loadMore} pageStart={pageStart} threshold={threshold} ref={refTBody}> 
      {
        items.length > 0 || !emptyText ?
          (
            items.map((item, index) => {
              const selected = item.id ? selection[item.id] : selection[index];

              return (
                <tr key={item.id || index} data-index={index} onClick={handleOnClick} onDoubleClick={handleOnDoubleClick}
                  className={selected ? 'selected' : undefined}>
                  {
                    columns.map((column, colIndex) => {
                      const currentCellIndex = getCellIndex(index, colIndex);
                      const isCellEditing = editing === currentCellIndex;

                      return (
                        <td key={colIndex} data-colindex={colIndex} data-colname={column} ref={el => {refCell.current[currentCellIndex] = el;}}
                          className={cx({'editing': isCellEditing})}>
                          { !isCellEditing ? itemRenderer(item, column, colIndex, currentCellIndex) : handleItemEditor(item, column, colIndex, currentCellIndex) }
                        </td>
                      )
                    })
                  }
                  {onCreateMenuItems && !disabled && <td className="menu"><Menu onCreateMenuItems={onCreateMenuItems} data={item}/></td>}
                </tr>
              )
            })
          ) : <tr><td className="empty-text">{ emptyText }</td></tr>
      }
    </InfiniteScroll>
  );

  return (
    <Fragment>
      <table {...rest} className={cls}>
        {children}
        {thead}
        {tbody}
      </table>
      { editing && <div className="core-table-click-overlay" onClick={handleOnEditCompleted} /> }
    </Fragment>
  );

});

Table.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.node),
  columnNames: PropTypes.arrayOf(PropTypes.node),
  disabled: PropTypes.bool,
  hasMore: PropTypes.func,
  itemRenderer: PropTypes.func,
  items: PropTypes.array,
  loadMore: PropTypes.func,
  onClick: PropTypes.func,
  onCreateMenuItems: PropTypes.func,
  onDoubleClick: PropTypes.func,
  onSelectionChanged: PropTypes.func,
  pageStart: PropTypes.func,
  selection: PropTypes.object,
  selectionType: PropTypes.string,
  threshold: PropTypes.number,
  itemEditor: PropTypes.func,
  onEditCompleted: PropTypes.func,
  emptyText: PropTypes.string,
};
Table.defaultProps = {
  itemRenderer: (item, column, columnIndex) => item[column],
  selection: {},
  selectionType: TableSelectionHandler.SINGLE,
};
Table.displayName = 'Table';
Table.NONE = TableSelectionHandler.NONE;
Table.SINGLE = TableSelectionHandler.SINGLE;
Table.MULTIPLE = TableSelectionHandler.MULTIPLE;

export default Table;
